import AsyncStorage from '@react-native-async-storage/async-storage';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Easing, Keyboard } from 'react-native';
import {
  ArticleKey,
  DEFAULT_ARTICLE_KEYS,
} from '@/screens/auth/welcome-screen-articles';
import { AUTH_MODAL_ROUTES } from '@/navigators/routes';
import { AuthModalStackParams } from '@/navigators/types';
import {
  AuthStackContext_GetGuestModeContentQuery,
  AuthStackContext_GetGuestModeContentQueryVariables,
  AuthStackContext_WhiteLabelConfigQuery,
  AuthStackContext_WhiteLabelConfigQueryVariables,
} from '@/types';
import { captureException } from '@/utils/sentry';
import { DynamicConfig } from './dynamic-config';
import { LoginMethod } from '@/enums';
import { openWebBrowserURL } from '@/utils/navigation';
import { partnerSupplier } from '@/utils/environment';
import {
  PUBLIC_RESTAURANT_QUERY,
  WHITELABEL_CONFIG_QUERY,
} from './auth-stack-context-queries';
import { StackNavigationProp } from '@react-navigation/stack';
import { useDemoSettingsContext } from './demo-settings-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const BASE_HEADER_HEIGHT = 60;

const INITIAL_HEADER_HEIGHT = 120;

const HEADER_ANIMATION_EASING = Easing.out(Easing.quad);

const PROSPECTIVE_CUSTOMER_SUBMITTED_KEY = 'prospectiveCustomerSubmitted';

type Navigation = StackNavigationProp<
  AuthModalStackParams,
  AUTH_MODAL_ROUTES.Base
>;

interface BecomeCustomerLabels {
  address: string;
  name: string;
}

interface PublicRestaurantDetails {
  address: string;
  categoryImagesEnabled: boolean;
  chatDynamicConfig: DynamicConfig;
  chatUUID: string;
  countryCode: string;
  displayItemCode: boolean;
  hidePlaceholderImages: boolean;
  name: string;
  restaurantUUID: string;
  supplierDynamicConfig: DynamicConfig;
  supplierUUID: string;
  timezone: string;
}

export interface AuthStackContextType {
  appBackgroundImageUrl: string | null;
  appBackgroundVideoUrl: string | null;
  appLandingLogoImageUrl: string | null;
  becomeCustomerLabels: BecomeCustomerLabels | null;
  becomeCustomerUrl: string | null;
  cmsArticles: ArticleKey[];
  cmsEnabled: boolean;
  defaultLoginMethod: LoginMethod;
  guestModeEnabled: boolean;
  headerHeight: Animated.Value;
  loginMethods: LoginMethod[];
  loginRequestEnabled: boolean;
  navigateToBecomeCustomer: () => void;
  navigateToLoginRecovery: () => void;
  prospectiveCustomerSubmitted: boolean;
  publicRestaurant: PublicRestaurantDetails | null;
  setProspectiveCustomerSubmitted: (submitted: boolean) => void;
  tagline: string | null;
  translationsEnabled: boolean;
  whitelabelConfigLoading: boolean;
}

export const AuthStackContext = createContext<AuthStackContextType | null>(
  null,
);

export const useMaybeAuthStackContext = () => useContext(AuthStackContext);

export const useAuthStackContext = () => {
  const context = useContext(AuthStackContext);
  if (!context) {
    throw new Error('useAuthStackContext requires an AuthStackContextProvider');
  }
  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const AuthStackContextProvider: React.FC<Props> = ({ children }) => {
  const navigation = useNavigation<Navigation>();
  const { demoSettings } = useDemoSettingsContext();
  const { top } = useSafeAreaInsets();
  const { appDimensions } = useLayoutContext();

  const canLoadPublicRestaurantRef = useRef(true);
  const [publicRestaurant, setPublicRestaurant] =
    useState<PublicRestaurantDetails | null>(null);
  const [prospectiveCustomerSubmitted, setProspectiveCustomerSubmitted] =
    useState(false);

  useEffect(() => {
    (async () => {
      try {
        const prospectiveCustomerAlreadySubmitted = await AsyncStorage.getItem(
          PROSPECTIVE_CUSTOMER_SUBMITTED_KEY,
        );
        setProspectiveCustomerSubmitted(
          prospectiveCustomerAlreadySubmitted === String(true),
        );
      } catch (e) {
        // Do nothing
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await AsyncStorage.setItem(
        PROSPECTIVE_CUSTOMER_SUBMITTED_KEY,
        String(prospectiveCustomerSubmitted),
      );
    })();
  }, [prospectiveCustomerSubmitted]);

  const businessOrganizationUUID = partnerSupplier.businessOrganizationUUID;

  const { data, loading } = useWrappedQuery<
    AuthStackContext_WhiteLabelConfigQuery,
    AuthStackContext_WhiteLabelConfigQueryVariables
  >(WHITELABEL_CONFIG_QUERY, {
    context: {
      anonymous: true,
    },
    fetchPolicy: 'network-only',
    variables: { businessOrganizationUUID },
  });
  const publicRestaurantQuery = useLazyQuery<
    AuthStackContext_GetGuestModeContentQuery,
    AuthStackContext_GetGuestModeContentQueryVariables
  >(PUBLIC_RESTAURANT_QUERY, {
    context: {
      anonymous: true,
    },
    fetchPolicy: 'network-only',
  });

  const headerHeightValueRef = useRef(
    new Animated.Value(INITIAL_HEADER_HEIGHT + top),
  );

  useEffect(() => {
    const showListener = Keyboard.addListener('keyboardWillShow', (e) => {
      Animated.timing(headerHeightValueRef.current, {
        duration: e.duration,
        easing: HEADER_ANIMATION_EASING,
        toValue: BASE_HEADER_HEIGHT + top,
        useNativeDriver: false,
      }).start();
    });

    const hideListener = Keyboard.addListener('keyboardWillHide', (e) => {
      Animated.timing(headerHeightValueRef.current, {
        duration: e.duration,
        easing: HEADER_ANIMATION_EASING,
        toValue: INITIAL_HEADER_HEIGHT + top,
        useNativeDriver: false,
      }).start();
    });

    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, [top]);

  useEffect(() => {
    (async () => {
      const guestModeEnabled =
        data?.getWhitelabelConfig.whitelabel_config?.guest_mode_enabled;
      if (guestModeEnabled && canLoadPublicRestaurantRef.current) {
        // Prevent any subsequent queries
        canLoadPublicRestaurantRef.current = false;

        try {
          const queryResult = await publicRestaurantQuery({
            variables: { businessOrganizationUUID },
          });
          const chat = queryResult.data?.getGuestModeContent[0]?.chat;

          const chatDynamicConfig: DynamicConfig = chat?.dynamic_config;
          const supplierDynamicConfig: DynamicConfig =
            chat?.supplier?.supplier_metadata?.dynamic_config;

          if (chat) {
            setPublicRestaurant({
              address: chat.restaurant.address.address,
              categoryImagesEnabled:
                chatDynamicConfig?.category_images_enabled ??
                supplierDynamicConfig?.category_images_enabled ??
                false,
              chatDynamicConfig,
              chatUUID: chat.uuid,
              countryCode: chat.supplier.address.country_code,
              displayItemCode:
                chat.supplier.supplier_metadata?.display_item_code ?? false,
              hidePlaceholderImages:
                chatDynamicConfig?.hide_placeholder_images ??
                supplierDynamicConfig?.hide_placeholder_images ??
                false,
              name: chat.restaurant.name,
              restaurantUUID: chat.restaurant.uuid,
              supplierDynamicConfig,
              supplierUUID: chat.supplier.uuid,
              timezone: chat.restaurant.address.timezone,
            });
          }
        } catch (e) {
          captureException(e);
        }
      }
    })();
  }, [data, businessOrganizationUUID, publicRestaurant, publicRestaurantQuery]);

  const value = useMemo(() => {
    const whitelabelConfig = data?.getWhitelabelConfig.whitelabel_config;
    const becomeCustomerUrl = whitelabelConfig?.become_customer_url ?? null;
    const backgroundVideoUrl =
      whitelabelConfig?.app_background_landscape_video_url &&
      appDimensions.width > appDimensions.height
        ? whitelabelConfig?.app_background_landscape_video_url
        : (whitelabelConfig?.app_background_video_url ?? null);
    return {
      appBackgroundImageUrl: whitelabelConfig?.app_background_image_url ?? null,
      appBackgroundVideoUrl: backgroundVideoUrl,
      appLandingLogoImageUrl:
        demoSettings?.landingPageLogo ??
        whitelabelConfig?.app_landing_logo_image ??
        whitelabelConfig?.ios_icon_image ??
        null,
      becomeCustomerLabels: data
        ? {
            address:
              data.getWhitelabelConfig.translated_become_customer_address_label,
            name: data.getWhitelabelConfig
              .translated_become_customer_name_label,
          }
        : null,
      becomeCustomerUrl,
      cmsArticles: demoSettings?.landingPageArticles ?? DEFAULT_ARTICLE_KEYS,
      cmsEnabled: whitelabelConfig?.cms_enabled ?? false,
      defaultLoginMethod:
        (whitelabelConfig?.default_login_method as LoginMethod | undefined) ??
        LoginMethod.Phone,
      guestModeEnabled: whitelabelConfig?.guest_mode_enabled ?? false,
      headerHeight: headerHeightValueRef.current,
      loginMethods: whitelabelConfig?.login_methods ?? [
        LoginMethod.Phone,
        LoginMethod.Email,
      ],
      loginRequestEnabled:
        (whitelabelConfig?.login_request_enabled &&
          data?.getWhitelabelConfig.render_contact_form) ??
        false,
      navigateToBecomeCustomer: () => {
        if (becomeCustomerUrl) {
          openWebBrowserURL(becomeCustomerUrl);
        } else {
          navigation.navigate(AUTH_MODAL_ROUTES.BecomeCustomer);
        }
      },
      navigateToLoginRecovery: () => {
        navigation.navigate(AUTH_MODAL_ROUTES.LoginRecovery);
      },
      prospectiveCustomerSubmitted,
      publicRestaurant,
      setProspectiveCustomerSubmitted,
      tagline:
        demoSettings?.landingPageTagline ??
        data?.getWhitelabelConfig.translated_tagline ??
        null,
      translationsEnabled: whitelabelConfig?.translations_enabled ?? false,
      whitelabelConfigLoading: loading,
    };
  }, [
    appDimensions.height,
    appDimensions.width,
    data,
    demoSettings,
    loading,
    navigation,
    prospectiveCustomerSubmitted,
    publicRestaurant,
  ]);

  return (
    <AuthStackContext.Provider value={value}>
      {children}
    </AuthStackContext.Provider>
  );
};
